/**
 * The Sites field on Invite User and Edit User (FM Manager R1 A10).
 *
 * Only the two FM roles are site-scoped. EMPTY MEANS ALL SITES, and the
 * server will not take `site_ids: []` on its own (422 "To give all sites, tick
 * All sites"), so "all" is sent as `all_sites: true` — see siteScopePayload.
 */
import { useBuildingOptions } from '@/hooks/useBuildingOptions';

export const SITE_SCOPED_ROLES: readonly string[] = ['fm_manager', 'fm_technician', 'reception'];

export function isSiteScopedRole(role: string | null | undefined): boolean {
  return !!role && SITE_SCOPED_ROLES.includes(role);
}

/**
 * 🔴 FM TECHNICIAN D5b: FOR A TECHNICIAN, EMPTY MEANS NO SITE, NOT ALL. The
 * server reads an empty list as "no access" for a technician (utils/site_scope
 * _empty_means_none), so "All sites" — which the server stores as an empty
 * list — would lock the new technician out of every job. For this role the
 * field offers no "All sites", needs at least one site, and says why. The
 * same role list as the server's FM_ROLES branch, narrowed to the technician.
 */
export const NEEDS_A_SITE_ROLES: readonly string[] = ['fm_technician', 'reception'];

export function needsASite(role: string | null | undefined): boolean {
  return !!role && NEEDS_A_SITE_ROLES.includes(role);
}

export const NEEDS_A_SITE_WHY =
  'A technician needs at least one site. "All sites" is not offered: with no site picked, a technician sees no jobs at all.';
export const RECEPTION_NEEDS_A_BUILDING_WHY =
  'Reception needs at least one building. "All sites" is not offered: with none picked, the front desk is empty.';

export interface SiteScopeValue { all: boolean; ids: string[] }

export const EMPTY_SITE_SCOPE: SiteScopeValue = { all: false, ids: [] };

/** The field's starting value for an existing user: their sites, or "all". */
export function siteScopeOf(siteIds: string[] | null | undefined, role?: string | null): SiteScopeValue {
  const ids = siteIds ?? [];
  return { all: ids.length === 0 && !needsASite(role), ids };
}

/** Why this value cannot be saved for this role, or null. */
export function siteScopeProblem(role: string | null | undefined, value: SiteScopeValue): string | null {
  if (!needsASite(role) || value.ids.length > 0) return null;
  return role === 'reception' ? RECEPTION_NEEDS_A_BUILDING_WHY : NEEDS_A_SITE_WHY;
}

/**
 * What to send. `initial` is the user's current sites when editing, or
 * undefined for an invite.
 *   - All sites ticked  -> { all_sites: true }
 *   - sites chosen      -> { site_ids }
 *   - nothing chosen    -> all sites (the field says "Leave empty for all sites")
 *   - unchanged (edit)  -> {} — neither key, so the server leaves them alone
 * An invite with nothing chosen also sends nothing: a new user starts on all sites.
 */
export function siteScopePayload(
  value: SiteScopeValue, initial?: string[] | null, role?: string | null,
): { site_ids?: string[]; all_sites?: boolean } {
  if (needsASite(role)) {
    // Never all_sites, never an empty list (D5b): the callers block the save
    // with siteScopeProblem; this sends only a real, changed list of sites.
    if (value.ids.length === 0) return {};
    const was = initial ?? [];
    const unchanged = initial !== undefined && was.length === value.ids.length
      && value.ids.every((id) => was.includes(id));
    return unchanged ? {} : { site_ids: value.ids };
  }
  const wantAll = value.all || value.ids.length === 0;
  if (initial === undefined) {
    if (value.all) return { all_sites: true };
    return value.ids.length ? { site_ids: value.ids } : {};
  }
  const before = initial ?? [];
  if (wantAll) return before.length === 0 ? {} : { all_sites: true };
  const same = before.length === value.ids.length && value.ids.every((id) => before.includes(id));
  return same ? {} : { site_ids: value.ids };
}

/** "3 sites", "1 site", or null when the row is not an FM role. "All sites" is the badge. */
export function siteCountLabel(role: string, siteIds: string[] | null | undefined): string | null {
  if (!isSiteScopedRole(role)) return null;
  const n = (siteIds ?? []).length;
  if (n === 0) return null;
  return n === 1 ? '1 site' : `${n} sites`;
}

export function SiteScopeBadge({ role, siteIds }: { role: string; siteIds: string[] | null | undefined }) {
  if (!isSiteScopedRole(role)) return null;
  const label = siteCountLabel(role, siteIds);
  if (!label && needsASite(role)) {
    return (
      <span className="rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-800"
        data-testid="user-no-site-badge" title={NEEDS_A_SITE_WHY}>No site yet</span>
    );
  }
  return label ? (
    <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-content-secondary"
      data-testid="user-sites-count">{label}</span>
  ) : (
    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700"
      data-testid="user-all-sites-badge">All sites</span>
  );
}

interface Props {
  id: string;
  role: string;
  value: SiteScopeValue;
  onChange: (next: SiteScopeValue) => void;
}

export default function SiteScopeField({ id, role, value, onChange }: Props) {
  const show = isSiteScopedRole(role);
  const buildingsQ = useBuildingOptions(show);
  if (!show) return null;
  const tech = needsASite(role);
  const problem = siteScopeProblem(role, value);
  const buildings = buildingsQ.data ?? [];
  const toggle = (bid: string) => {
    const ids = value.ids.includes(bid) ? value.ids.filter((x) => x !== bid) : [...value.ids, bid];
    onChange({ all: false, ids });
  };
  return (
    <fieldset className="space-y-2" data-testid="site-scope-field">
      <legend className="mb-1 block text-sm font-medium text-content-secondary">Sites</legend>
      {!tech && (
        <label className="flex items-center gap-2 text-sm text-content-secondary" htmlFor={`${id}-all`}>
          <input id={`${id}-all`} type="checkbox" className="h-4 w-4" checked={value.all}
            onChange={(e) => onChange(e.target.checked ? { all: true, ids: [] } : { all: false, ids: value.ids })} />
          All sites
        </label>
      )}
      <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-line p-2">
        {buildingsQ.isLoading && <p className="text-xs text-content-subtle">Loading sites…</p>}
        {!buildingsQ.isLoading && buildings.length === 0 && (
          <p className="text-xs text-content-subtle">No sites found.</p>
        )}
        {buildings.map((b) => (
          <label key={b.id} className="flex items-center gap-2 text-sm text-content-secondary" htmlFor={`${id}-${b.id}`}>
            <input id={`${id}-${b.id}`} type="checkbox" className="h-4 w-4" disabled={value.all && !tech}
              checked={(tech || !value.all) && value.ids.includes(b.id)} onChange={() => toggle(b.id)} />
            <span className="[overflow-wrap:anywhere]">{b.name}</span>
          </label>
        ))}
      </div>
      {tech ? (
        <p className={`text-xs ${problem ? 'text-warning-800' : 'text-content-subtle'}`}
           data-testid="site-scope-needs-a-site">{NEEDS_A_SITE_WHY}</p>
      ) : (
        <p className="text-xs text-content-subtle">Leave empty for all sites.</p>
      )}
    </fieldset>
  );
}
